import {SpotLightLabels, ThresholdConfig} from '../connectivity-guru.contracts';
import {DEFAULT_RSSI_RANGE, RssiRangeConfig} from '../../../shared/image-layers/layer.types';

const colorLow = '#40DE7A';
const colorMedium = '#DDE20D';
const colorHigh = '#FF983D';
const colorCritical = '#E11D48';
const colorGray = 'rgba(107, 114, 128, 1)'; // Gray
export const CG_INFORMATIVE_COLOR = 'rgba(30, 153, 253, 1)';
export const CG_ERROR_COLOR = 'rgba(230, 81, 0, 1)';
export const CG_WIFI_SCAN_TIMEOUT = 5000;

export class WifiScanService {
    public getIconColor(level: number): string {
        switch (level) {
            case 1:
                return colorGray;
            case 2:
                return colorCritical; // Red (#E11D48)
            case 3:
                return colorHigh; // Orange (#FF983D)
            case 4:
                return colorLow; // Green (#40DE7A)
            default:
                return colorGray;
        }
    }

    public getCurrentColorByPoint(points: number): string {
        const thresholdLow = 2;
        const thresholdMedium = 5;
        const thresholdHigh = 10;

        if (points <= thresholdLow) {
            return colorLow;
        } else if (points > thresholdLow && points <= thresholdMedium) {
            return colorMedium;
        } else if (points > thresholdMedium && points <= thresholdHigh) {
            return colorHigh;
        }

        return colorCritical;
    }

    private checkAvailableValueForThreshold(label: string, value: number): number {
        const thresholdAvailable = 1;
        const thresholdNotAvailable = 0;

        switch (label) {
            case SpotLightLabels.signal:
                return value < 0 ? thresholdAvailable : thresholdNotAvailable;

            case SpotLightLabels.upload:
            case SpotLightLabels.download:
                return value > 0 ? thresholdAvailable : thresholdNotAvailable;

            default:
                return thresholdAvailable;
        }
    }

    public evalThreshold(
        label: SpotLightLabels,
        value: number,
        threshold: ThresholdConfig,
        higherIsBetter: boolean
    ): number {
        const thresholdMode = {unsupported: 0, na: 1, critical: 2, warning: 3, fine: 4};

        if (!threshold.enabled) return thresholdMode.unsupported;

        const {critical, warning} = threshold;

        if (this.checkAvailableValueForThreshold(label, value)) {
            if (higherIsBetter) {
                if (value <= critical) return thresholdMode.critical;
                if (value > critical && value <= warning) return thresholdMode.warning;
                if (value > warning) return thresholdMode.fine;
            } else {
                if (value >= critical) return thresholdMode.critical;
                if (value < critical && value >= warning) return thresholdMode.warning;
                if (value < warning) return thresholdMode.fine;
            }
        }
        return thresholdMode.na;
    }

    /**
     * Colors an RSSI value using the scan's per-account RSSI range (minRSSI/midRSSI/maxRSSI),
     * the same source the 3D heatmap, RSSI scale bar and native app rely on. This keeps the
     * Network Data tab aligned with the account configuration instead of hard-coded thresholds.
     */
    public getDbmColor(dbm: number | undefined, rssiConfig?: RssiRangeConfig): string {
        const {minRSSI, midRSSI} = {...DEFAULT_RSSI_RANGE, ...rssiConfig};

        const dbmGreen = 'dbm-green';
        const dbmRed = 'dbm-red';
        const dbmYellow = 'dbm-yellow';

        if (dbm == null) {
            return '';
        }
        if (dbm > midRSSI) {
            return dbmGreen;
        }
        if (dbm < minRSSI) {
            return dbmRed;
        }
        return dbmYellow;
    }
}
